import { findContactPoint } from '@lifeomic/fhir-constants';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { isAxiosError } from 'axios';
import { phone } from 'phone';

export type MemberContactField = 'email' | 'phone';

/** Uses the same `phone` package as subject search; validates to E.164-capable numbers. */
export function isValidMemberPhone(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  return phone(t).isValid;
}

function normalizeMemberPhoneForStorage(value: string): string {
  const t = value.trim();
  const result = phone(t);
  return result.isValid ? result.phoneNumber : t;
}

/** E.164 when the value is a valid number; otherwise the trimmed input (for display). */
export function formatMemberPhoneForDisplay(value: string): string {
  const t = value.trim();
  if (!t) return '';
  const result = phone(t);
  return result.isValid ? result.phoneNumber : t;
}

/** Raw telecom value as shown in onboarding snapshot (matches formatMember). */
export function getRawContactValue(
  patient: WithPlatformDefaults<fhir3.Patient>,
  field: MemberContactField,
): string {
  if (field === 'email') {
    return findContactPoint(patient.telecom, { system: 'email' })?.value ?? '';
  }
  return (
    findContactPoint(patient.telecom, {
      system: 'phone',
      use: 'mobile',
    })?.value ||
    findContactPoint(patient.telecom, { system: 'phone' })?.value ||
    ''
  );
}

export function applyContactUpdate(
  patient: WithPlatformDefaults<fhir3.Patient>,
  field: MemberContactField,
  newValue: string,
): WithPlatformDefaults<fhir3.Patient> {
  const telecom = [...(patient.telecom || [])];
  if (field === 'email') {
    const idx = telecom.findIndex(t => t.system === 'email');
    if (idx >= 0) {
      telecom[idx] = { ...telecom[idx], value: newValue };
    } else {
      telecom.push({ system: 'email', value: newValue });
    }
  } else {
    const mobileIdx = telecom.findIndex(
      t => t.system === 'phone' && t.use === 'mobile',
    );
    const anyPhoneIdx = telecom.findIndex(t => t.system === 'phone');
    const idx = mobileIdx >= 0 ? mobileIdx : anyPhoneIdx;
    const stored = normalizeMemberPhoneForStorage(newValue);
    if (idx >= 0) {
      telecom[idx] = { ...telecom[idx], value: stored };
    } else {
      telecom.push({ system: 'phone', use: 'mobile', value: stored });
    }
  }
  return { ...patient, telecom };
}

/** Both fields match, are non-empty, and differ from the current value (ready to show confirmation). */
export function canProceedToContactConfirm(args: {
  first: string;
  second: string;
  oldValue: string;
  field?: MemberContactField;
}): boolean {
  const a = args.first.trim();
  const b = args.second.trim();
  if (!a) return false;
  if (a !== b) return false;
  if (a === args.oldValue.trim()) return false;
  if (args.field === 'phone' && !isValidMemberPhone(a)) return false;
  return true;
}

export function canSubmitContactEdit(args: {
  first: string;
  second: string;
  oldValue: string;
  confirmed: boolean;
  field?: MemberContactField;
}): boolean {
  if (!args.confirmed) return false;
  return canProceedToContactConfirm(args);
}

export function memberOpsRequestErrorMessage(err: unknown): string | undefined {
  if (!isAxiosError(err)) {
    return undefined;
  }
  const data: unknown = err.response?.data;
  if (typeof data === 'string' && data.trim().length > 0) {
    return data;
  }
  if (data && typeof data === 'object') {
    const payload = data as { message?: unknown; error?: unknown };
    if (typeof payload.message === 'string' && payload.message.length > 0) {
      return payload.message;
    }
    if (typeof payload.error === 'string' && payload.error.length > 0) {
      return payload.error;
    }
  }
  return undefined;
}
